import { useEffect, useState } from 'react';
import { getAttachPublicPath, isAttachFile, isAttachFileWithoutCloudType } from 'reactApp/modules/attaches/attaches.helpers';
import { Ovidius } from 'reactApp/modules/ovidius';
import type { OvidiusPart } from 'reactApp/modules/ovidius/types';
import { OvidiusEvents } from 'reactApp/utils/analytics/types';

import { type UseOvidiusData, type UseOvidiusError, type UseOvidiusProps, FileError } from './types';

export const useHomeOvidius = ({ file, storage, sendRadar }: UseOvidiusProps): UseOvidiusData => {
    const [partsAmount, setPartsAmount] = useState<number>(0);
    const [content, setContent] = useState<ArrayBuffer[]>([]);
    const [partsInfo, setPartsInfo] = useState<Array<OvidiusPart>>([]);
    const [error, setError] = useState<UseOvidiusError | null>(null);

    useEffect(() => {
        setPartsAmount(0);
        setContent([]);
        setPartsInfo([]);
        setError(null);
    }, [file, storage]);

    useEffect(() => {
        if (isAttachFileWithoutCloudType(file)) {
            return;
        }

        const publicPath = isAttachFile(file) ? getAttachPublicPath(file.id) : undefined;
        const controller = new AbortController();

        (async () => {
            const { partsInfo, firstPart } = await Ovidius.getPdfFileInfo({ file, storage, publicPath, signal: controller.signal });

            if (!partsInfo) {
                sendRadar(OvidiusEvents.NO_INFO, false, true);
                setError(FileError.Info);
                return;
            }

            if (!firstPart) {
                sendRadar(OvidiusEvents.NO_F_PART, false, true);
                setError(FileError.FirstPart);
                return;
            }

            const { parts, parts_total: partsTotal } = partsInfo;

            setPartsInfo(parts);
            setContent([firstPart]);
            setPartsAmount(partsTotal);

            if (parts.length === 1) {
                sendRadar(OvidiusEvents.ONE_PART);
            } else {
                sendRadar(OvidiusEvents.FIRST_PART);
            }
        })();

        return () => {
            controller.abort();
        };
    }, [file, storage]);

    useEffect(() => {
        if (!partsInfo.length) {
            return;
        }

        if (partsInfo.length === 1) {
            sendRadar(OvidiusEvents.ALL_PARTS);
            return;
        }

        const publicPath = isAttachFile(file) ? getAttachPublicPath(file.id) : undefined;
        const controller = new AbortController();

        (async () => {
            const getPdfFilePartInfo = Ovidius.getPdfFilePart({ file, storage, publicPath, signal: controller.signal });

            for (let i = 1; i < partsInfo.length; i++) {
                const { id } = partsInfo[i];
                const { content: newPart } = await getPdfFilePartInfo(id);

                if (!newPart) {
                    sendRadar(OvidiusEvents.NO_PART, false, true);
                    setError(FileError.SecondPart);
                    return;
                }

                setContent((oldContent) => [...oldContent, newPart]);
            }

            sendRadar(OvidiusEvents.ALL_PARTS);
        })();

        return () => {
            controller.abort();
        };
    }, [partsInfo]);

    return {
        content,
        partsAmount,
        error,
    };
};
